import React, { useState, useEffect } from 'react';

/**
 * Sidebar — collapsible navigation grouped by workflow area.
 *
 * Top-level nav: Dashboard, Tenants (always visible, ungrouped).
 * Groups (collapsible):
 *   - Activity           — Pending, Unknown, Duplicate, Maintenance
 *   - Property & Compliance — Compliance, Available Properties, Leads
 *   - Tenancy Breach     — Arrears, Section 8
 * Bottom: Settings.
 *
 * Each group header shows an aggregate count pill when collapsed so the
 * agent never loses sight of an unread alert. When the group is expanded,
 * individual item badges remain visible.
 *
 * Props:
 *   onNav(id): item click handler — `id` is one of the item keys below.
 *   counts:   { pending, whatsapp, duplicates, maintenance, compliance,
 *               available, leads, arrears, section8 }
 *   activeView:  'dashboard' | 'tenants' | 'settings'   (highlights that row)
 *   onDashboard: () => void
 *   onTenants:   () => void
 *   onSettings:  () => void
 */

const ICON = {
  property: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  dashboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  tenants: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  pending: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg>,
  whatsapp: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>,
  duplicates: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>,
  maintenance: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>,
  compliance: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  available: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  leads: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  arrears: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 7c0-5.333-8-5.333-8 0"/><path d="M10 7v14"/><path d="M6 21h12"/><path d="M6 13h10"/></svg>,
  section8: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  settings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
  // 2026-06-04 — Hardhat/workman helmet icon so Contractors is visually
  // distinct from Maintenance (which uses the wrench/spanner glyph).
  contractors: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 17h16"/><path d="M3 17v-1a9 9 0 0 1 18 0v1"/><path d="M9 8V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/><line x1="12" y1="8" x2="12" y2="14"/></svg>,
  chevron: <svg className="eiq-group-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>,
};

const WHATSAPP_ENABLED = String(process.env.REACT_APP_WHATSAPP_ENABLED || 'false').toLowerCase() === 'true';

const GROUPS = [
  {
    id: 'activity',
    label: 'Activity',
    items: [
      { id: 'pending-messages', icon: 'pending', label: 'Pending Messages', countKey: 'pending', badgeClass: '' },
      ...(WHATSAPP_ENABLED
        ? [{ id: 'whatsapp-alerts', icon: 'whatsapp', label: 'Unknown Contacts', countKey: 'whatsapp', badgeClass: 'eiq-nav-badge-amber' }]
        : []),
      { id: 'duplicate-alerts', icon: 'duplicates', label: 'Duplicate Contacts', countKey: 'duplicates', badgeClass: 'eiq-nav-badge-brown' },
      { id: 'maintenance-board', icon: 'maintenance', label: 'Maintenance', countKey: 'maintenance', badgeClass: 'eiq-nav-badge-grey' },
      // 2026-06-04 — Contractors lives under Maintenance per agent feedback.
      // Uses customAction so the parent component's onContractors handler
      // is called instead of the default onNav scroll behaviour.
      { id: 'contractors', icon: 'contractors', label: 'Contractors', countKey: 'contractors', badgeClass: 'eiq-nav-badge-grey', customAction: 'contractors' },
    ],
  },
  {
    id: 'property',
    label: 'Property & Compliance',
    items: [
      { id: 'compliance-status', icon: 'compliance', label: 'Compliance', countKey: 'compliance', badgeClass: 'eiq-nav-badge-amber' },
      { id: 'leads-section', icon: 'leads', label: 'Leads', countKey: 'leads', badgeClass: 'eiq-nav-badge-teal' },
    ],
  },
  {
    id: 'breach',
    label: 'Tenancy Breach',
    items: [
      { id: 'arrears-overview', icon: 'arrears', label: 'Arrears', countKey: 'arrears', badgeClass: 'eiq-nav-badge-green' },
      { id: 'active-cases', icon: 'section8', label: 'Section 8', countKey: 'section8', badgeClass: 'eiq-nav-badge-blue' },
    ],
  },
];

export default function Sidebar({
  onNav,
  counts = {},
  activeView = 'dashboard',
  onDashboard,
  onLandlords,
  onProperties,
  onPropertiesAvailable,
  onPropertiesLet,
  onContractors,
  onOnboarding,
  showOnboarding = false,
  onSettings,
}) {
  // Default all groups expanded. Persist collapse state to localStorage so
  // the agent's preference survives a refresh.
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem('eiq_sidebar_groups_v1');
      if (raw) return JSON.parse(raw);
    } catch (_e) { /* ignore */ }
    return { activity: true, property: true, breach: true };
  });

  // 2026-06-04 — Auto-expand the Activity group when the agent navigates
  // to the Contractors view (Contractors lives nested under Maintenance).
  useEffect(() => {
    if (activeView === 'contractors') {
      setOpenGroups(prev => prev.activity ? prev : { ...prev, activity: true });
    }
  }, [activeView]);

  // 2026-05-29 (CHANGE 1) — Properties is now a collapsible dropdown
  // with two sub-items: Available and Let. Clicking the parent toggles
  // the dropdown; clicking a sub-item routes to the filtered list.
  const [propertiesOpen, setPropertiesOpen] = useState(() => {
    try {
      const raw = localStorage.getItem('eiq_sidebar_props_open_v1');
      if (raw) return JSON.parse(raw);
    } catch (_e) { /* ignore */ }
    return false;
  });
  const togglePropertiesOpen = () => {
    setPropertiesOpen(prev => {
      const next = !prev;
      try {
        localStorage.setItem('eiq_sidebar_props_open_v1', JSON.stringify(next));
      } catch (_e) { /* ignore */ }
      return next;
    });
  };

  const toggle = (groupId) => {
    setOpenGroups(prev => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      try { localStorage.setItem('eiq_sidebar_groups_v1', JSON.stringify(next)); } catch (_e) { /* ignore */ }
      return next;
    });
  };

  const groupAggregate = (group) =>
    group.items.reduce((sum, item) => sum + (counts[item.countKey] || 0), 0);

  return (
    <nav className="eiq-sidebar" data-testid="sidebar-nav">
      <div className="eiq-sidebar-section">
        <button className={`eiq-nav-item ${activeView === 'dashboard' ? 'active' : ''}`}
                onClick={onDashboard}
                data-testid="nav-dashboard">
          {ICON.dashboard}
          Dashboard
        </button>
        {/* CHANGE 1 — Properties now a collapsible dropdown with two
            sub-items: Available + Let. Tenants is no longer a top-
            level item — tenant info is accessed by clicking through
            from the Let list. */}
        <button className={`eiq-nav-item ${activeView === 'properties' || activeView === 'properties-let' ? 'active' : ''}`}
                onClick={togglePropertiesOpen}
                aria-expanded={propertiesOpen}
                data-testid="nav-properties">
          {ICON.property}
          Properties
          <span
            className="eiq-group-chev"
            style={{
              marginLeft: 'auto',
              transform: propertiesOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s',
              display: 'inline-flex',
            }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                 stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </span>
        </button>
        {propertiesOpen && (
          <div className="eiq-nav-group-items" data-testid="nav-properties-children">
            <button
              type="button"
              className={`eiq-nav-item eiq-nav-item-nested ${activeView === 'properties' ? 'active' : ''}`}
              onClick={onPropertiesAvailable || onProperties}
              data-testid="nav-properties-available">
              {ICON.available}
              Available
              {(counts.available || 0) > 0 && (
                <span className="eiq-nav-badge eiq-nav-badge-pink">{counts.available}</span>
              )}
            </button>
            <button
              type="button"
              className={`eiq-nav-item eiq-nav-item-nested ${activeView === 'properties-let' ? 'active' : ''}`}
              onClick={onPropertiesLet || onProperties}
              data-testid="nav-properties-let">
              {ICON.tenants}
              Let
              {(counts.let || 0) > 0 && (
                <span className="eiq-nav-badge eiq-nav-badge-green">{counts.let}</span>
              )}
            </button>
          </div>
        )}
        {/* Step 45 — Landlords top-level nav entry. */}
        <button className={`eiq-nav-item ${activeView === 'landlords' ? 'active' : ''}`}
                onClick={onLandlords}
                data-testid="nav-landlords">
          {ICON.tenants}
          Landlords
        </button>
        {/* Onboarding moved to Settings (2026-06-01). Clicking
            Settings → Onboarding opens the same wizard. */}

        {GROUPS.map(group => {
          const isOpen = !!openGroups[group.id];
          const agg = groupAggregate(group);
          return (
            <div key={group.id} className="eiq-nav-group" data-testid={`nav-group-${group.id}`}>
              <button
                type="button"
                className={`eiq-nav-group-header ${isOpen ? 'open' : 'collapsed'}`}
                onClick={() => toggle(group.id)}
                aria-expanded={isOpen}
                data-testid={`nav-group-toggle-${group.id}`}
              >
                {ICON.chevron}
                <span className="eiq-nav-group-label">{group.label}</span>
                {!isOpen && agg > 0 && (
                  <span className="eiq-nav-badge eiq-nav-badge-group"
                        data-testid={`nav-group-agg-${group.id}`}>
                    {agg}
                  </span>
                )}
              </button>
              {isOpen && (
                <div className="eiq-nav-group-items">
                  {group.items.map(item => {
                    const c = counts[item.countKey] || 0;
                    const isCustomContractors = item.customAction === 'contractors';
                    const handleClick = isCustomContractors
                      ? () => onContractors && onContractors()
                      : () => onNav(item.id);
                    const isActive = isCustomContractors && activeView === 'contractors';
                    return (
                      <button key={item.id}
                              className={`eiq-nav-item eiq-nav-item-nested ${isActive ? 'active' : ''}`}
                              onClick={handleClick}
                              data-testid={isCustomContractors ? 'nav-contractors' : `nav-${item.countKey}`}>
                        {ICON[item.icon]}
                        {item.label}
                        {c > 0 && (
                          <span className={`eiq-nav-badge ${item.badgeClass}`}>{c}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="eiq-sidebar-section eiq-sidebar-footer">
        <button className={`eiq-nav-item ${activeView === 'settings' ? 'active' : ''}`}
                onClick={onSettings}
                data-testid="nav-settings">
          {ICON.settings}
          Settings
        </button>
      </div>
    </nav>
  );
}
